import React, { useState, useEffect, Fragment, useRef } from 'react';

import ListPartial from '@/features/livestream/detail/partials/list';
import ChatPartial from '@/features/livestream/detail/partials/chat';
import PlayerPartial from '@/features/livestream/detail/partials/player';
import SearchPartial from '@/components/form/SearchVideo';
import { useTranslation } from 'next-i18next';
import { productsRangeBuilder } from '@/features/livestream/detail/util';
import { LiveVideo } from '@/features/livestream/shared';
import {
  PinnedProductTypes,
  PinnedProductDefault,
  LiveStreamStatus,
  LiveStreamProduct,
  MessageData,
  Comment,
} from '@/features/livestream/detail/const';
import Breadcrumb from './partials/breadcrumb';
import MetaTag from '@/components/ui/MetaTag';
import { getAuth } from '@/libs/cookies';
import { useFetchTrackingVideo } from './api';
import { LotteryGameStatus, ProductStatus, SocketChanel } from '@/layouts/shared';
export type LiveVideoDetailLayoutProps = {
  liveVideo: LiveVideo;
  isAuth: boolean;
  collectionStickers: any;
  comments: (MessageData | Comment)[];
  offset: number;
};
const LiveVideoDetailLayout = ({
  liveVideo,
  isAuth,
  collectionStickers,
  comments,
  offset,
}: LiveVideoDetailLayoutProps) => {
  const { t } = useTranslation(['live-video', 'layout']);
  const getPinnedProduct = (productsRange: LiveStreamProduct[]) => {
    if (productsRange.length <= 0) return null;
    const p = productsRange.find((p) => p.status === LiveStreamStatus.LIVE_NOW);
    if (p && p.product_id && p.products?.status !== ProductStatus.Hide) {
      return p;
    }
    return null;
  };
  const [pinnedProduct, setPinnedProduct] = useState<PinnedProductTypes | undefined>();
  useEffect(() => {
    setPinnedProduct(getPinnedProduct(liveVideo.live_stream_product) || PinnedProductDefault);
  }, [liveVideo]);
  const [showLotteryBtn, setShowLotteryBtn] = useState<boolean>(false);
  const [message, setMessage] = useState<any | null>(null);
  const [waitingToReconnect, setWaitingToReconnect] = useState<boolean | null>(null);
  const [retry, setRetry] = useState<number>(0);
  const ref = useRef<any>(null);
  const timeOut = useRef<any>(null);
  const clientRef = useRef<any>(null);

  const { refetch: refetchTracking } = useFetchTrackingVideo(liveVideo.id, {
    enabled: false,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    const { isAuth, token } = getAuth();
    if (waitingToReconnect) {
      return;
    }
    if (!isAuth) refetchTracking();
    if (isAuth && !clientRef.current) {
      const ws = new WebSocket(`${process.env.WEBSOCKET_URL || 'wss://api-dev.anthu.tech/ws/realtime'}?token=${token}`);
      clientRef.current = ws;
      ws.onopen = () => {
        let socketPayload = {
          method: 'subscribe',
          type: 'unicast',
          channels: [{ id: SocketChanel.LiveStream, object_id: liveVideo.id }],
        };
        ws.send(JSON.stringify(socketPayload));
        if (!retry) {
          refetchTracking();
          setRetry((prev) => prev + 1);
        }
        timeOut.current = setInterval(() => {
          let socketPayload = { method: 'ping' };
          ws.send(JSON.stringify(socketPayload));
        }, 15000);
      };

      ws.onmessage = (evt: any) => {
        const m = JSON.parse(evt.data);
        if (m?.data) {
          setMessage(m);
          clearTimeout(ref.current);
        }
        ref.current = setTimeout(() => {
          setMessage(null);
        }, 5000);
      };
      ws.onclose = () => {
        if (clientRef.current) {
          console.log('ws closed by server');
        } else {
          console.log('ws closed by app component unmount');
        }
        setWaitingToReconnect(true);
        setTimeout(() => setWaitingToReconnect(null), 5000);
      };
      return () => {
        ws.close();
        console.log('closed');
        clearInterval(timeOut.current);
        timeOut.current = null;
        clientRef.current = null;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVideo.id, waitingToReconnect]);

  const lotteryGame = liveVideo.lottery_game?.find((g) => g.status !== LotteryGameStatus.Ended) || null;
  return (
    <Fragment>
      <MetaTag title={t('detail.title')} description={t('detail.title')} image={liveVideo.images[0].url || ''} />
      <div className="container tw-pt-8 tw-pb-12 tw-mx-auto md:tw-border-t tw-border-primary-main">
        <Breadcrumb title={liveVideo.name} />
        <h1 className="tw-hidden"> {liveVideo.name}</h1>
        <h2 className="tw-hidden"> {liveVideo.name}</h2>
        <h2 className="tw-hidden"> {liveVideo.description}</h2>
        <div className="tw-flex tw-flex-col tw-mt-6">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-y-4 tw-gap-x-0 lg:tw-gap-x-4 lg:tw-gap-y-0">
            <div className="lg:tw-col-span-7 tw-relative tw-flex tw-flex-col">
              <SearchPartial t={t} />
              <div className="tw-relative tw-mt-4 tw-h-full tw-flex tw-flex-1 tw-rounded-5 tw-overflow-hidden">
                {liveVideo.link && (
                  <PlayerPartial
                    url={liveVideo.link}
                    liveImage={liveVideo.images[0]?.url}
                    pinnedProduct={pinnedProduct}
                    setPinnedProduct={setPinnedProduct}
                    liveStreamId={liveVideo.id}
                    liveStatus={liveVideo.status}
                    productsRange={productsRangeBuilder(liveVideo.live_stream_product)}
                    messageData={message}
                    lotteryGame={lotteryGame}
                    luckyDetail={liveVideo?.lucky}
                    t={t}
                    isAuth={isAuth}
                    showLotteryBtn={() => setShowLotteryBtn(true)}
                    hideLotteryBtn={() => setShowLotteryBtn(false)}
                    sticker={message?.data?.sticker_icon}
                  />
                )}
              </div>
            </div>
            <div className="lg:tw-col-span-5">
              <ChatPartial
                hideLotteryBtn={() => setShowLotteryBtn(false)}
                showLotteryBtn={showLotteryBtn}
                liveStreamId={liveVideo.id}
                status={liveVideo.status}
                pinnedProduct={pinnedProduct}
                commentList={comments}
                isAuth={isAuth}
                collectionStickers={collectionStickers}
                messageData={message}
                lotteryGame={lotteryGame}
                defaultOffset={offset}
                products={liveVideo.live_stream_product}
                t={t}
                retry={waitingToReconnect || false}
              />
            </div>
          </div>
          <div className="tw-mt-16">
            <ListPartial t={t} />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default LiveVideoDetailLayout;
